import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { SoftButton, SoftRowSkeleton, cls } from "../hatim/soft/SoftKit";
import { useAccessRequestsQuery, useDecideAccessRequestMutation } from "../../api/libraryApi";
import { REPLY_MAX, formatDay, pendingIncomingCount, requestMeta, requesterWarning } from "./libraryRules";
import { CenterMessage, LIBRARY_HOME, LibraryPanel, MessageDialog, Segment, packagePath } from "./LibraryKit";

const ROLES = [
  { value: "incoming", key: "lib_req_incoming" },
  { value: "outgoing", key: "lib_req_outgoing" },
];

/**
 * İzin istekleri (mobil library/requests): Gelen isteklerde isteyenin adı,
 * paket, grup üyeliği uyarısı ve kopya notu; bekleyende Onayla / Reddet
 * (isteğe bağlı cevapla). Giden isteklerde durum ve cevap, paket açılır.
 * Yön adreste: ?yon=giden.
 */
export default function AccessRequestsPage() {
  const { t } = useScriptMode();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const role = params.get("yon") === "giden" ? "outgoing" : "incoming";
  const { data: items = [], isLoading, isFetching, isError, error, refetch } = useAccessRequestsQuery({ role });
  const [decide, { isLoading: busy }] = useDecideAccessRequestMutation();
  const [deciding, setDeciding] = useState(null);

  const send = async (message) => {
    if (!deciding) return;
    const { request, action } = deciding;
    const result = await decide({ requestId: request.id, action, message });
    if (result.error) {
      toast.error(result.error.message || t("lib_action_failed"));
      refetch();
      return;
    }
    setDeciding(null);
    toast.success(t(action === "approve" ? "lib_access_granted" : "lib_request_rejected"));
  };

  const pendingCount = role === "incoming" ? pendingIncomingCount(items) : 0;

  const renderIncoming = (r) => {
    // Karar düğmeleri yalnız sunucu "karar verebilirsin" dediğinde.
    const pending = r.status === "pending" && r.canDecide;
    return (
      <li key={r.id} className={`px-4 py-3 flex flex-col gap-1.5 ${cls.row}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-base truncate flex-1 ${cls.title}`}>{r.requesterName}</span>
          <span className={`text-xs shrink-0 ${cls.muted}`}>{formatDay(r.createdAt)}</span>
        </div>
        <p className={`text-sm ${cls.title}`}>
          {r.packageId ? (
            <Link to={packagePath(r.packageId)} className={`${cls.accent} hover:underline`}>
              {r.packageTitle}
            </Link>
          ) : (
            r.packageTitle
          )}{" "}
          {t("lib_wants_to_see")}
        </p>
        <p className={`flex items-start gap-1.5 text-xs ${cls.warning}`}>
          <ErrorOutlineIcon sx={{ fontSize: 15 }} aria-hidden />
          <span>{requesterWarning(r, t)}</span>
        </p>
        {r.requesterHasCopy ? (
          <p className={`flex items-start gap-1.5 text-xs ${cls.muted}`}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
            <span>{t("lib_has_copy")}</span>
          </p>
        ) : null}
        {pending ? (
          <div className="flex gap-2 pt-1">
            <SoftButton tone="cancel" size="small" onClick={() => setDeciding({ request: r, action: "reject" })} disabled={busy}>
              {t("lib_reject")}
            </SoftButton>
            <SoftButton
              tone="success"
              size="small"
              startIcon={<CheckIcon fontSize="small" />}
              onClick={() => setDeciding({ request: r, action: "approve" })}
              disabled={busy}
            >
              {t("lib_approve")}
            </SoftButton>
          </div>
        ) : (
          <p className={`text-xs ${cls.muted}`}>{requestMeta(r, t)}</p>
        )}
      </li>
    );
  };

  const renderOutgoing = (r) => {
    const Icon = r.status === "approved" ? LockOpenOutlinedIcon : LockOutlinedIcon;
    const body = (
      <>
        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
          <Icon sx={{ fontSize: 18 }} className={r.status === "rejected" ? cls.danger : undefined} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-base truncate ${cls.title}`}>{r.packageTitle}</span>
          <span className={`block text-xs ${cls.muted}`}>
            {[formatDay(r.decidedAt ?? r.createdAt), requestMeta(r, t)].filter(Boolean).join(" · ")}
          </span>
        </span>
      </>
    );
    return (
      <li key={r.id}>
        {r.packageId ? (
          <Link to={packagePath(r.packageId)} className={`flex items-center gap-3 px-4 py-3 ${cls.row} ${cls.rowHover}`}>
            {body}
            <ChevronRightIcon fontSize="small" className={cls.muted} />
          </Link>
        ) : (
          <div className={`flex items-center gap-3 px-4 py-3 ${cls.row}`}>{body}</div>
        )}
      </li>
    );
  };

  return (
    <LibraryPanel title={t("lib_requests_title")} subtitle={t("lib_requests_row_hint")} back={LIBRARY_HOME}>
      <div className={`px-4 py-3 flex items-center justify-between gap-2 ${cls.row}`}>
        <Segment
          value={role}
          options={ROLES}
          label={t("lib_requests_title")}
          onChange={(next) => setParams(next === "outgoing" ? { yon: "giden" } : {}, { replace: true })}
        />
        {pendingCount > 0 ? <span className={`text-xs ${cls.muted}`}>{`${pendingCount} ${t("lib_pending_requests")}`}</span> : null}
      </div>
      {isLoading ? (
        <SoftRowSkeleton rows={4} />
      ) : isError ? (
        <CenterMessage text={error?.message || t("lib_requests_failed")} danger onRetry={refetch} error={error} />
      ) : items.length === 0 ? (
        <CenterMessage text={t("lib_no_requests")} />
      ) : (
        <ul className={`tk-stagger ${isFetching ? "opacity-70 transition-opacity" : ""}`}>
          {items.map((r) => (role === "incoming" ? renderIncoming(r) : renderOutgoing(r)))}
        </ul>
      )}

      <MessageDialog
        open={deciding?.action === "reject"}
        title={t("lib_reject")}
        hint={t("lib_reject_request_hint")}
        placeholder={t("lib_reply_optional")}
        sendLabel={t("lib_reject")}
        danger
        maxLength={REPLY_MAX}
        busy={busy}
        onSend={send}
        onClose={() => setDeciding(null)}
      />
      <MessageDialog
        open={deciding?.action === "approve"}
        title={t("lib_approve")}
        hint={t("lib_approve_hint")}
        placeholder={t("lib_reply_optional")}
        sendLabel={t("lib_approve")}
        maxLength={REPLY_MAX}
        busy={busy}
        onSend={send}
        onClose={() => setDeciding(null)}
      />
    </LibraryPanel>
  );
}
